import React, { useState, useEffect, useRef } from 'react';
import { checklistService, Checklist, ChecklistItem } from '@/firebase/checklistService';
import { complianceService, ComplianceTemplate } from '@/firebase/complianceService';
import { locationService, Location } from '@/firebase/locationService';
import { assetService, AssetType } from '@/firebase/assetService';
import { useNotification } from '@/contexts/NotificationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { normalizeSubscriptionPlan } from '@/firebase/subscriptionService';
import { useAuth } from '@/contexts/AuthContext';
import { usePreview } from '@/contexts/PreviewContext';
import { 
  fetchUserRoles,
  normalizeRoles,
  getUserSystemRoleForOrganization,
  getUserOwnerRolesForOrganization,
  isSystemUserInOrganization,
  isManagerInOrganization
} from '@/utils/roleHelpers';
import { SYSTEM_ROLES } from '@/constants/roles';
import { isRoleValidForOrganizationType } from '@/constants/organizationRoles';
import { Plus, Trash2, Pencil, Play, Sparkles, AlertCircle, FileUp, Loader2, X, History, Info } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ChecklistForm from './ChecklistForm';
import ChecklistQuickSetupWizard from './ChecklistQuickSetupWizard';
import RunChecklist from './RunChecklist';
import ChecklistHistory from './ChecklistHistory';
import { aiSearchService, NewChecklistSuggestion } from '@/services/aiSearchService';
import ContextualHelp from './ContextualHelp';

const ChecklistManagement: React.FC = () => {
  const { showNotification } = useNotification();
  const { currentOrganization } = useOrganization();
  const { previewState, isPreviewMode } = usePreview();
  const { hasFeature, subscription } = useSubscription();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [templates, setTemplates] = useState<Record<string, ComplianceTemplate>>({});
  const [locations, setLocations] = useState<Record<string, Location>>({});
  const [assetTypes, setAssetTypes] = useState<Record<string, AssetType>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedChecklist, setSelectedChecklist] = useState<Checklist | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isViewingHistory, setIsViewingHistory] = useState(false);
  const [checklistForHistory, setChecklistForHistory] = useState<Checklist | null>(null);
  const [newChecklistSuggestions, setNewChecklistSuggestions] = useState<NewChecklistSuggestion[] | null>(null);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [checklistToAdd, setChecklistToAdd] = useState<Partial<Checklist> | null>(null);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [isProcessingImport, setIsProcessingImport] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isQuickSetupOpen, setIsQuickSetupOpen] = useState(false);
  const [quickSetupInitialData, setQuickSetupInitialData] = useState<{
    name?: string;
    description?: string;
    items?: ChecklistItem[];
    templateId?: string;
  } | undefined>(undefined);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const [userOwnerRoles, setUserOwnerRoles] = useState<string[]>([]);
  const [isSystemUser, setIsSystemUser] = useState<boolean>(false);
  const [isSystemManager, setIsSystemManager] = useState<boolean>(false);
  const [userLocationIds, setUserLocationIds] = useState<string[]>([]);
  const MAX_IMPORT_FILE_SIZE = 20 * 1024 * 1024; // 20 MB
  const canUseAIImport = hasFeature('ai_assistance');
  const allowedMimeTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];

  useEffect(() => {
    loadUserRoles();
    loadUserLocations();
  }, [currentOrganization?.id, user?.id, isPreviewMode, previewState?.previewType, previewState?.previewUserId, previewState?.role]);

  useEffect(() => {
    loadData();
  }, [currentOrganization?.id, user?.id, userLocationIds.length, isSystemUser]);

  // Handle history query parameter
  useEffect(() => {
    const historyChecklistId = searchParams.get('history');
    if (historyChecklistId && checklists.length > 0) {
      const checklist = checklists.find(c => c.id === historyChecklistId);
      if (checklist) {
        setChecklistForHistory(checklist);
        setIsViewingHistory(true);
        // Remove query parameter from URL
        setSearchParams({}, { replace: true });
      }
    }
  }, [checklists, searchParams, setSearchParams]);

  const loadUserRoles = async () => {
    // In preview mode, use preview user's roles
    if (isPreviewMode && previewState?.previewType === 'user' && previewState.previewUserId) {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const previewUserRef = doc(db, 'users', previewState.previewUserId);
        const previewUserSnap = await getDoc(previewUserRef);
        
        if (previewUserSnap.exists()) {
          const previewUserData = previewUserSnap.data();
          const organizationId = currentOrganization?.id;
          
          // Use organization-specific roles if organization is available
          if (organizationId) {
            const ownerRoles = getUserOwnerRolesForOrganization(previewUserData, organizationId);
            setUserOwnerRoles(ownerRoles);
            setIsSystemUser(isSystemUserInOrganization(previewUserData, organizationId));
            setIsSystemManager(isManagerInOrganization(previewUserData, organizationId));
          } else {
            // Fallback to global roles if no organization
            const previewUserRoles = normalizeRoles(previewUserData?.roles || previewUserData?.role);
            const ownerRoles = previewUserRoles.filter((r: string) => !SYSTEM_ROLES.includes(r.toLowerCase() as any));
            setUserOwnerRoles(ownerRoles);
            const systemRoles = previewUserRoles.filter((r: string) => SYSTEM_ROLES.includes(r.toLowerCase() as any));
            const isSystemUserRole = systemRoles.length === 1 && systemRoles[0].toLowerCase() === 'user';
            setIsSystemUser(isSystemUserRole);
            const isManagerRole = previewUserRoles.some((r: string) => ['manager', 'admin'].includes(r.toLowerCase()));
            setIsSystemManager(isManagerRole);
          }
        }
      } catch (error) {
        console.error('Error loading preview user roles:', error);
      }
    } else if (isPreviewMode && previewState?.previewType === 'role') {
      // Preview by role: determine permissions based on role
      const role = previewState.role.toLowerCase();
      const isSystemUserRole = role === 'user';
      const isManagerRole = role === 'manager' || role === 'admin';
      
      setIsSystemUser(isSystemUserRole);
      setIsSystemManager(isManagerRole);
      setUserOwnerRoles([]); // No owner roles when previewing by role
    } else if (user?.id && currentOrganization?.id) {
      // Normal mode: use actual user's roles for current organization
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const organizationId = currentOrganization.id;
          
          // Get organization-specific roles
          const ownerRoles = getUserOwnerRolesForOrganization(userData, organizationId);
          const isSystemUserRole = isSystemUserInOrganization(userData, organizationId);
          const isManagerRole = isManagerInOrganization(userData, organizationId);
          
          setUserOwnerRoles(ownerRoles);
          setIsSystemUser(isSystemUserRole);
          setIsSystemManager(isManagerRole);
        } else {
          // Fallback to legacy fetchUserRoles
          const roles = await fetchUserRoles(user.id);
          setUserOwnerRoles(roles.ownerRoles);
          setIsSystemUser(roles.isSystemUser);
          setIsSystemManager(roles.isSystemManager);
        }
      } catch (error) {
        console.error('Error loading user roles:', error);
        // Fallback to legacy fetchUserRoles
        const roles = await fetchUserRoles(user.id);
        setUserOwnerRoles(roles.ownerRoles);
        setIsSystemUser(roles.isSystemUser);
        setIsSystemManager(roles.isSystemManager);
      }
    } else if (user?.id && !currentOrganization?.id) {
      // No organization - use legacy roles
      const roles = await fetchUserRoles(user.id);
      setUserOwnerRoles(roles.ownerRoles);
      setIsSystemUser(roles.isSystemUser);
      setIsSystemManager(roles.isSystemManager);
    }
  };

  const loadUserLocations = async () => {
    // In preview mode, use preview user's locationIds
    if (isPreviewMode && previewState?.previewType === 'user' && previewState.locationIds) {
      setUserLocationIds(previewState.locationIds);
    } else if (isPreviewMode && previewState?.previewType === 'role') {
      // Preview by role: no location restrictions (show all)
      setUserLocationIds([]);
    } else if (user?.id && currentOrganization?.id) {
      // Normal mode: use actual user's locationIds
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const locationIds = userData.locationIds || [];
          setUserLocationIds(locationIds);
        }
      } catch (error) {
        console.error('Error loading user locations:', error);
        setUserLocationIds([]);
      }
    } else {
      setUserLocationIds([]);
    }
  };

  // Debounced AI search when no results are found
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Clear AI suggestion when search term is cleared
    if (!searchTerm) {
      setNewChecklistSuggestions(null);
      return;
    }

    // Calculate filtered checklists inline to check if we have results
    const currentFiltered = checklists.filter(checklist =>
      checklist.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      checklist.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Only trigger AI search if there are no filtered checklists and search term is long enough
    const hasResults = currentFiltered.length > 0;
    
    if (!hasResults && searchTerm.length >= 3) {
      // Debounce AI search by 800ms
      searchTimeoutRef.current = setTimeout(async () => {
        setIsLoadingAI(true);
        setNewChecklistSuggestions(null);
        
        try {
          // First, search default templates for matches (if user has no templates, AI can't help)
          const existingChecklistNames = checklists.map(c => c.name);
          const availableTemplates = Object.values(templates);
          
          // Check if user has any templates first
          if (availableTemplates.length === 0) {
            // No templates available - can't create checklists without templates
            setIsLoadingAI(false);
            // Set a special "no templates" state so we can show a helpful message
            setNewChecklistSuggestions([] as any); // Empty array to trigger the AI message display
            return;
          }
          
          // Try AI generation for checklists
          const newSuggestions = await aiSearchService.generateNewChecklistSuggestions(
            searchTerm, 
            existingChecklistNames,
            availableTemplates
          );
          
          if (newSuggestions && newSuggestions.length > 0) {
            setNewChecklistSuggestions(newSuggestions);
          } else {
            // No suggestions from AI - show message that AI search is working but found nothing
            setNewChecklistSuggestions([] as any);
          }
        } catch (error) {
          console.error('Error fetching AI checklist suggestions:', error);
        } finally {
          setIsLoadingAI(false);
        }
      }, 800);
    } else {
      setNewChecklistSuggestions(null);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, checklists, templates]);

  const loadData = async () => {
    // Don't load data if no organization
    if (!currentOrganization?.id) {
      setTemplates({});
      setChecklists([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    
    // Load templates for current organization
    const templatesResult = await complianceService.getAllTemplates(currentOrganization.id);
    if (templatesResult.success && templatesResult.data) {
      const templateMap: Record<string, ComplianceTemplate> = {};
      templatesResult.data.forEach(template => {
        if (template.id) {
          templateMap[template.id] = template;
        }
      });
      setTemplates(templateMap);
    }
    
    // Load locations for current organization
    const locationsResult = await locationService.getAllLocations(currentOrganization.id);
    if (locationsResult.success && locationsResult.data) {
      const locationMap: Record<string, Location> = {};
      locationsResult.data.forEach(location => {
        if (location.id) {
          locationMap[location.id] = location;
        }
      });
      setLocations(locationMap);
    }
    
    // Load asset types for current organization
    const assetTypesResult = await assetService.getAllAssetTypes(currentOrganization.id);
    if (assetTypesResult.success && assetTypesResult.data) {
      const assetTypeMap: Record<string, AssetType> = {};
      assetTypesResult.data.forEach(assetType => {
        if (assetType.id) {
          assetTypeMap[assetType.id] = assetType;
        }
      });
      setAssetTypes(assetTypeMap);
    }
    
    // Load checklists for current organization
    const result = await checklistService.getAllChecklists(currentOrganization.id);
    if (result.success && result.data) {
      setChecklists(result.data);
    } else {
      showNotification(`Error loading checklists: ${result.error}`, 'error');
    }
    setIsLoading(false);
  };

  const loadChecklists = async () => {
    await loadData();
  };

  const handleAdd = () => {
    // Check if user has permission to add checklists (only admin or manager)
    if (!isSystemManager) {
      showNotification('Only administrators and managers can add checklists', 'error');
      return;
    }
    if (!currentOrganization?.id) {
      setShowOrgModal(true);
      return;
    }
    setSelectedChecklist(null);
    setIsAdding(true);
  };

  const resetImportState = () => {
    setImportFile(null);
    setImportError(null);
    setIsDragActive(false);
    if (uploadInputRef.current) {
      uploadInputRef.current.value = '';
    }
  };

  const validateImportFile = (file: File): string | null => {
    if (file.size > MAX_IMPORT_FILE_SIZE) {
      return 'File is too large. Please upload a document smaller than 20 MB.';
    }

    const fileName = file.name.toLowerCase();
    const isAllowedType =
      allowedMimeTypes.includes(file.type) ||
      file.type.startsWith('image/') ||
      fileName.endsWith('.pdf') ||
      fileName.endsWith('.doc') ||
      fileName.endsWith('.docx') ||
      fileName.endsWith('.png') ||
      fileName.endsWith('.jpg') ||
      fileName.endsWith('.jpeg');

    if (!isAllowedType) {
      return 'Unsupported file type. Please upload a PDF, Word document, or image file.';
    }

    return null;
  };

  const assignImportFile = (file: File | null) => {
    if (!file) {
      setImportFile(null);
      setImportError(null);
      return;
    }

    const validationError = validateImportFile(file);
    if (validationError) {
      setImportError(validationError);
      setImportFile(null);
      if (uploadInputRef.current) {
        uploadInputRef.current.value = '';
      }
      return;
    }

    setImportFile(file);
    setImportError(null);
  };

  const handleOpenImportModal = () => {
    // Check if user has permission to import checklists (only admin or manager)
    if (!isSystemManager) {
      showNotification('Only administrators and managers can import checklists', 'error');
      return;
    }
    if (!currentOrganization?.id) {
      setShowOrgModal(true);
      return;
    }
    resetImportState();
    setIsImportModalOpen(true);
  };

  const handleCloseImportModal = () => {
    setIsImportModalOpen(false);
    resetImportState();
  };

  const handleImportFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    assignImportFile(file || null);
  };

  const handleImportDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(true);
  };

  const handleImportDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
  };

  const handleImportDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);

    const file = event.dataTransfer.files?.[0];
    if (!file) {
      return;
    }

    assignImportFile(file);
  };

  const handleProcessImport = async () => {
    if (!currentOrganization?.id) {
      setIsImportModalOpen(false);
      setShowOrgModal(true);
      return;
    }

    if (!importFile) {
      setImportError('Please choose a checklist file to upload.');
      return;
    }

    if (!canUseAIImport) {
      setImportError('Upgrade to Standard or Pro to use AI checklist import.');
      return;
    }

    setIsProcessingImport(true);
    setImportError(null);

    try {
      const { extractChecklistFromDocument, isGeminiConfigured } = await import('@/services/geminiService');

      if (!isGeminiConfigured()) {
        throw new Error('Gemini AI is not configured. Please contact support.');
      }

      const extraction = await extractChecklistFromDocument(importFile);
      const rawItems = extraction.items || [];

      const normalizedItems = rawItems.map((item, index) => {
        const baseText = item.text?.trim() || `Step ${index + 1}`;
        return {
          order: item.order || index + 1,
          text: baseText, // Use clean text (already cleaned in extraction)
          evidenceDefinitions: item.evidenceDefinitions || undefined, // Preserve evidence definitions
        };
      });

      if (normalizedItems.length === 0) {
        throw new Error('No checklist steps were identified in that document. Please upload a clearer checklist.');
      }

      const variables = extraction.variables && extraction.variables.length > 0
        ? extraction.variables.map(name => ({
            name,
            possibleValues: [],
          }))
        : undefined;

      const descriptionParts: string[] = [];
      if (extraction.description?.trim()) {
        descriptionParts.push(extraction.description.trim());
      }
      if (extraction.confidenceNotes?.trim()) {
        descriptionParts.push(`AI notes: ${extraction.confidenceNotes.trim()}`);
      }

      const checklistData: Partial<Checklist> = {
        name: extraction.name,
        description: descriptionParts.length > 0 ? descriptionParts.join('\n\n') : undefined,
        items: normalizedItems,
        variables,
        templateId: '',
      };

      setChecklistToAdd(checklistData);
      setIsAdding(true);
      setSelectedChecklist(null);
      setIsEditing(false);
      setSearchTerm('');
      setIsImportModalOpen(false);
      resetImportState();

      showNotification('Checklist draft created from your document. Review, assign a compliance template, and save.', 'success');
    } catch (error: any) {
      console.error('Checklist import failed:', error);
      const message = typeof error?.message === 'string'
        ? error.message
        : 'Failed to analyze the document. Please try again with a clearer file.';
      setImportError(message);
    } finally {
      setIsProcessingImport(false);
    }
  };

  const handleEdit = (checklist: Checklist) => {
    // Check if user has permission to edit checklists (only admin or manager)
    if (!isSystemManager) {
      showNotification('Only administrators and managers can edit checklists', 'error');
      return;
    }
    setSelectedChecklist(checklist);
    setIsEditing(true);
  };

  const handleDelete = async (id: string) => {
    // Check if user has permission to delete checklists (only admin or manager)
    if (!isSystemManager) {
      showNotification('Only administrators and managers can delete checklists', 'error');
      return;
    }
    if (!confirm('Are you sure you want to delete this checklist?')) return;

      const userInfo = user ? { id: user.id, name: user.name || 'Unknown', email: user.email || 'Unknown' } : undefined;
      const result = await checklistService.deleteChecklist(id, userInfo, currentOrganization?.id);
      if (result.success) {
        await loadChecklists();
        showNotification('Checklist deleted successfully', 'success');
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
  };


  const handleRunChecklist = (checklist: Checklist) => {
    // Check if user is on free plan
    const normalizedPlan = subscription?.plan ? normalizeSubscriptionPlan(subscription.plan) : null;
    const isFreePlan = !subscription || !subscription.plan || normalizedPlan === 'communicate';
    const isSubscriptionCancelled = subscription?.status === 'canceled';
    
    if (isFreePlan && !isSubscriptionCancelled) {
      setShowUpgradeModal(true);
      return;
    }
    
    setSelectedChecklist(checklist);
    setIsRunning(true);
  };

  const handleViewHistory = (checklist: Checklist) => {
    setChecklistForHistory(checklist);
    setIsViewingHistory(true);
  };

  const handleCloseRunChecklist = () => {
    setIsRunning(false);
    setSelectedChecklist(null);
    loadChecklists();
  };

  const handleFormSubmit = async (data: Partial<Checklist>) => {
    // Check if user has permission (only admin or manager)
    if (!isSystemManager) {
      showNotification('Only administrators and managers can add or edit checklists', 'error');
      setIsAdding(false);
      setIsEditing(false);
      return;
    }

    if (isAdding) {
      if (!currentOrganization?.id) {
        setShowOrgModal(true);
        setIsAdding(false);
        return;
      }
      // Ensure required fields are present
      if (!data.name || !data.templateId) {
        showNotification('Name and Compliance are required', 'error');
        return;
      }
      const userInfo = user ? { id: user.id, name: user.name || 'Unknown', email: user.email || 'Unknown' } : undefined;
      const result = await checklistService.addChecklist(data as Omit<Checklist, 'id'>, currentOrganization?.id, userInfo);
      if (result.success) {
        await loadChecklists();
        showNotification('Checklist added successfully', 'success');
        setIsAdding(false);
        setChecklistToAdd(null);
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    } else if (isEditing && selectedChecklist?.id) {
      const userInfo = user ? { id: user.id, name: user.name || 'Unknown', email: user.email || 'Unknown' } : undefined;
      const result = await checklistService.updateChecklist(selectedChecklist.id, data, userInfo, currentOrganization?.id);
      if (result.success) {
        await loadChecklists();
        showNotification('Checklist updated successfully', 'success');
        setIsEditing(false);
        setChecklistToAdd(null);
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    }
  };

  const handleAddFromSuggestion = (suggestion: NewChecklistSuggestion) => {
    // Check if user has permission to add checklists (only admin or manager)
    if (!isSystemManager) {
      showNotification('Only administrators and managers can add checklists', 'error');
      return;
    }
    if (!currentOrganization?.id) {
      setShowOrgModal(true);
      return;
    }

    // Open Quick Setup wizard with pre-filled data from AI suggestion
    setQuickSetupInitialData({
      name: suggestion.name,
      description: suggestion.description || '',
      items: suggestion.items,
      templateId: suggestion.templateId
    });
    setIsQuickSetupOpen(true);
    // Clear search to see the new checklist after creation
    setSearchTerm('');
  };

  const handleFormCancel = () => {
    setIsAdding(false);
    setIsEditing(false);
    setSelectedChecklist(null);
    setChecklistToAdd(null);
  };

  // Filter checklists
  const filteredChecklists = checklists.filter(checklist => {
    // Apply search filter
    const matchesSearch = checklist.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      checklist.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    // For users with system role "User", filter by owner roles AND location access
    if (isSystemUser) {
      // If checklist has no compliance template, don't show it to regular users
      if (!checklist.templateId) {
        return false;
      }
      
      // Check if the compliance template's owner_role matches user's owner roles
      const template = templates[checklist.templateId];
      if (!template) return false;
      
      const templateOwnerRole = template.owner_role;
      if (!templateOwnerRole) return false;
      
      // Validate role against organization type
      const organizationType = currentOrganization?.industryType || null;
      if (!isRoleValidForOrganizationType(templateOwnerRole, organizationType)) {
        return false;
      }
      if (!userOwnerRoles.includes(templateOwnerRole)) {
        return false;
      }
      
      // Filter by location access
      // If checklist has no locationIds specified, it's available for all locations (skip location filter)
      // If checklist has locationIds, user must have access to at least one of those locations
      if (checklist.locationIds && checklist.locationIds.length > 0) {
        // User must have access to at least one location in the checklist's locationIds
        const hasLocationAccess = checklist.locationIds.some(locationId => 
          userLocationIds.includes(locationId)
        );
        if (!hasLocationAccess) {
          return false;
        }
      }
      
      return true;
    }
    
    // Admin and Manager system roles see all checklists
    return true;
  });

  return (
    <div className="p-4 max-w-[95vw] mx-auto relative">
      {/* Backdrop overlay when panel is open */}
      {(isAdding || isEditing) && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-30 z-40 transition-opacity"
          onClick={handleFormCancel}
        />
      )}
      
      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-semibold text-gray-800">Checklist Management</h2>
            <ContextualHelp category="Checklists" tooltip="Help with Checklists" />
          </div>
          <div className="flex gap-2">
            {isSystemManager && (
              <button
                onClick={() => {
                  if (!currentOrganization?.id) {
                    setShowOrgModal(true);
                    return;
                  }
                  setIsQuickSetupOpen(true);
                }}
                className="btn-primary px-4 py-2 flex items-center gap-2"
                title="Create checklist, template, and asset type in one flow"
              >
                <Plus className="w-4 h-4" /> Add New Checklist
              </button>
            )}
            <button onClick={loadChecklists} className="btn-secondary px-4 py-2">
              Refresh
            </button>
          </div>
        </div>

        {/* Educational Banner for Free Plan */}
        {(() => {
          const normalizedPlan = subscription?.plan ? normalizeSubscriptionPlan(subscription.plan) : null;
          const isFreePlan = !subscription || !subscription.plan || normalizedPlan === 'communicate';
          const isSubscriptionCancelled = subscription?.status === 'canceled';
          
          if (isFreePlan && !isSubscriptionCancelled) {
            return (
              <div className="mb-6 bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <Info className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">Checklists Available</h3>
                    <p className="text-sm text-blue-800 mb-4">
                      You can create and manage checklists on your current plan. However, <strong>running checklists requires an Operate or Comply plan</strong>. Upgrade to enable running checklists and tracking completion.
                    </p>
                    <button
                      onClick={() => navigate('/subscription')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium inline-flex items-center gap-2"
                    >
                      <Sparkles className="w-4 h-4" />
                      Upgrade Plan
                    </button>
                  </div>
                </div>
              </div>
            );
          }
          return null;
        })()}

        {/* Info Banner */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>How it works:</strong> Create reusable checklists (e.g., Daily Safety Checks, Monthly Inspections) and tie them to compliance items. Then run these checklists per location and check off completed items.
          </p>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search checklists... (AI-powered suggestions available)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md pr-10"
            />
            {isLoadingAI && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-orange-500 animate-spin" />
                <span className="text-xs text-orange-600 font-medium animate-pulse">AI searching...</span>
              </div>
            )}
          </div>
        </div>

        {/* Checklists Table */}
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading checklists...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto border-collapse">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Name</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Description</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Compliance</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Asset Types</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Locations</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Variables</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 border border-gray-200">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredChecklists.map((checklist, index) => (
                  <tr key={checklist.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100`}>
                    <td className="px-3 py-2 border border-gray-200">
                      <div className="font-medium text-sm text-gray-900">{checklist.name}</div>
                    </td>
                    <td className="px-3 py-2 border border-gray-200">
                      <div className="text-xs text-gray-700">
                        {checklist.description || 'No description'}
                        <span className="text-gray-500 ml-2">({checklist.items?.length || 0} items)</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 border border-gray-200">
                      {checklist.templateId && templates[checklist.templateId] ? (
                        <span className="text-xs text-gray-900">{templates[checklist.templateId].name}</span>
                      ) : checklist.templateId ? (
                        <span className="text-xs text-gray-700 italic">{checklist.templateId}</span>
                      ) : (
                        <span className="text-xs text-gray-500 italic">Not assigned</span>
                      )}
                    </td>
                    <td className="px-3 py-2 border border-gray-200">
                      {(() => {
                        // Find asset types linked to this checklist's compliance template
                        const linkedAssetTypes = checklist.templateId
                          ? Object.values(assetTypes).filter(
                              at => at.complianceTemplateIds?.includes(checklist.templateId!)
                            )
                          : [];
                        
                        if (linkedAssetTypes.length > 0) {
                          return (
                            <div className="flex flex-wrap gap-1">
                              {linkedAssetTypes.slice(0, 3).map((assetType) => (
                                <span key={assetType.id} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-800">
                                  {assetType.name}
                                </span>
                              ))}
                              {linkedAssetTypes.length > 3 && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-600">
                                  +{linkedAssetTypes.length - 3} more
                                </span>
                              )}
                            </div>
                          );
                        } else {
                          return <span className="text-xs text-gray-500 italic">Not assigned</span>;
                        }
                      })()}
                    </td>
                    <td className="px-3 py-2 border border-gray-200">
                      {checklist.locationIds && checklist.locationIds.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {checklist.locationIds.slice(0, 3).map((locationId) => {
                            const location = locations[locationId];
                            return location ? (
                              <span key={locationId} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-800">
                                {location.name}
                              </span>
                            ) : (
                              <span key={locationId} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-500 italic">
                                {locationId}
                              </span>
                            );
                          })}
                          {checklist.locationIds.length > 3 && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                              +{checklist.locationIds.length - 3} more
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500 italic">All locations</span>
                      )}
                    </td>
                    <td className="px-3 py-2 border border-gray-200">
                      {checklist.variables && checklist.variables.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {checklist.variables.map((variable, idx) => {
                            const varName = typeof variable === 'string' ? variable : variable.name;
                            const varValues = typeof variable === 'string' ? [] : (variable.possibleValues || []);
                            return (
                              <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" title={varValues.length > 0 ? `${varName} (${varValues.length} values)` : varName}>
                                {varName}
                                {varValues.length > 0 && (
                                  <span className="ml-1 text-blue-600 font-normal">
                                    ({varValues.length})
                                  </span>
                                )}
                            </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500">None</span>
                      )}
                    </td>
                    <td className="px-3 py-2 border border-gray-200">
                      <div className="flex justify-center gap-1">
                        <button
                          onClick={() => handleRunChecklist(checklist)}
                          className="text-purple-600 hover:text-purple-800 text-xs px-2 py-1"
                          title="Run Checklist"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleViewHistory(checklist)}
                          className="text-indigo-600 hover:text-indigo-800 text-xs px-2 py-1"
                          title="View History"
                        >
                          <History className="w-4 h-4" />
                        </button>
                        {isSystemManager && (
                          <>
                            <button
                              onClick={() => handleEdit(checklist)}
                              className="text-green-600 hover:text-green-800 text-xs px-2 py-1"
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(checklist.id!)}
                              className="text-red-600 hover:text-red-800 text-xs px-2 py-1"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {filteredChecklists.length === 0 && !isLoading && (
          <div className="text-center py-8">
            {isLoadingAI ? (
              <div className="border border-gray-200 rounded p-6">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Sparkles className="w-6 h-6 text-orange-500 animate-spin" />
                  <p className="text-lg font-medium text-gray-700">AI is finding suggestions...</p>
                </div>
                <p className="text-sm text-gray-500">Analyzing available templates for you</p>
              </div>
            ) : newChecklistSuggestions && newChecklistSuggestions.length === 0 && searchTerm.length >= 3 ? (
              <div className="border border-orange-200 rounded-lg p-6 bg-orange-50 text-left">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-orange-600" />
                  <h3 className="text-lg font-semibold text-orange-900">AI Search Complete</h3>
                </div>
                
                <p className="text-sm text-gray-700 mb-2">
                  {Object.keys(templates).length === 0 
                    ? '⚠️ No compliance templates available. Please create or load default templates first before creating checklists.'
                    : 'No AI-generated checklist suggestions found. Try a different search term or create a checklist manually.'}
                </p>
                
                {Object.keys(templates).length === 0 && (
                  <button
                    onClick={() => window.location.href = '/compliance-management'}
                    className="mt-3 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm"
                  >
                    Go to Templates →
                  </button>
                )}
              </div>
            ) : newChecklistSuggestions && newChecklistSuggestions.length > 0 ? (
              <div className="border border-blue-200 rounded-lg p-6 bg-blue-50 text-left">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-blue-900">New Checklist Suggestions</h3>
                </div>
                
                <p className="text-sm text-gray-700 mb-4">
                  Based on compliance requirements, here are checklists you might want to add to your system:
                </p>

                {/* Show upgrade message for Starter plan */}
                {!hasFeature('ai_assistance') && (
                  <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <p className="text-sm text-orange-800 font-medium mb-2">
                      Upgrade to Standard or Pro plan to add AI-generated checklists
                    </p>
                    <button
                      onClick={() => navigate('/subscription')}
                      className="px-3 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium inline-flex items-center gap-1"
                    >
                      <Sparkles className="w-3 h-3" />
                      Upgrade Now
                    </button>
                  </div>
                )}
                
                <div className="space-y-4">
                  {newChecklistSuggestions.map((suggestion, index) => (
                    <div 
                      key={index}
                      className="bg-white rounded-lg p-5 border border-blue-200 hover:border-blue-300 hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h5 className="font-semibold text-gray-900 text-lg mb-1">{suggestion.name}</h5>
                          {suggestion.description && (
                            <p className="text-sm text-gray-700 mb-2">{suggestion.description}</p>
                          )}
                          <div className="flex items-center gap-3 mb-2">
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                              {suggestion.templateName}
                            </span>
                            {suggestion.variables.length > 0 && (
                              <span className="text-xs text-gray-600">
                                Variables: {suggestion.variables.join(', ')}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-600 italic mb-3">"{suggestion.why_relevant}"</p>
                          
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="text-xs text-gray-700">
                              <span className="font-medium">Checklist Items ({suggestion.items.length}):</span>
                              <ul className="mt-1 space-y-1 ml-4 list-disc">
                                {suggestion.items.slice(0, 5).map((item, idx) => (
                                  <li key={idx} className="text-gray-600">{item.text}</li>
                                ))}
                                {suggestion.items.length > 5 && (
                                  <li className="text-gray-500 italic">... and {suggestion.items.length - 5} more items</li>
                                )}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => {
                          if (hasFeature('ai_assistance')) {
                            handleAddFromSuggestion(suggestion);
                          } else {
                            navigate('/subscription');
                          }
                        }}
                        disabled={!hasFeature('ai_assistance')}
                        className={`w-full mt-4 px-4 py-2 rounded-md transition-colors flex items-center justify-center gap-2 font-medium ${
                          hasFeature('ai_assistance')
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                        }`}
                      >
                        <Plus className="w-4 h-4" />
                        {hasFeature('ai_assistance') ? 'Add This Checklist' : 'Upgrade to Add'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 border border-gray-200 rounded">
                <p className="text-lg font-medium">No checklists found</p>
                <p className="text-sm mt-1">
                  {searchTerm 
                    ? 'Try a different search term or clear filters.' 
                    : 'No checklists match your filters.'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Upload from Document Modal */}
      {isImportModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[180] flex items-center justify-center p-4"
          onClick={handleCloseImportModal}
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-5 border-b border-gray-200 bg-gray-50 rounded-t-lg">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Upload Checklist (AI)</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Upload an existing checklist (PDF, Word, or image). Complin AI will draft the steps so you can view and save.
                </p>
              </div>
              <button
                onClick={handleCloseImportModal}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                  isDragActive
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-300 hover:border-primary-500'
                }`}
                onClick={() => uploadInputRef.current?.click()}
                onDragOver={handleImportDragOver}
                onDragEnter={handleImportDragOver}
                onDragLeave={handleImportDragLeave}
                onDrop={handleImportDrop}
              >
                <input
                  ref={uploadInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,image/*"
                  className="hidden"
                  onChange={handleImportFileChange}
                />
                <div className="flex flex-col items-center gap-3">
                  <FileUp className="w-10 h-10 text-primary-500" />
                  <div>
                    <p className="text-sm font-semibold text-gray-800">
                      Click to choose a file or drag it here
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Supported: PDF, Word (.doc, .docx), PNG, JPG • Max 20 MB
                    </p>
                  </div>
                  {importFile && (
                    <div className="bg-gray-100 border border-gray-200 rounded-md px-3 py-2 text-xs text-gray-700 w-full">
                      <p className="font-medium truncate">{importFile.name}</p>
                      <p>
                        {(importFile.size / (1024 * 1024)).toFixed(2)} MB • {importFile.type || 'Unknown type'}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {!canUseAIImport && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-md text-sm text-orange-800">
                  Upgrade to Standard or Pro to unlock AI checklist imports.
                </div>
              )}

              {importError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                  {importError}
                </div>
              )}

              <ul className="text-xs text-gray-600 list-disc list-inside space-y-1">
                <li>We keep your original file private—it’s used only to draft this checklist.</li>
                <li>You can edit every item before saving.</li>
                <li>AI notes, if any, will appear in the description for quick review.</li>
              </ul>
            </div>

            <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200 rounded-b-lg bg-gray-50">
              <button
                onClick={handleCloseImportModal}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleProcessImport}
                className={`px-4 py-2 rounded-md text-sm font-medium inline-flex items-center gap-2 transition-colors ${
                  importFile && canUseAIImport && !isProcessingImport
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                }`}
                disabled={!importFile || !canUseAIImport || isProcessingImport}
              >
                {isProcessingImport ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Convert to Checklist
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Setup Wizard */}
      {isQuickSetupOpen && (
        <ChecklistQuickSetupWizard
          initialData={quickSetupInitialData}
          onComplete={(checklistId) => {
            setIsQuickSetupOpen(false);
            setQuickSetupInitialData(undefined);
            loadChecklists();
            showNotification('Checklist setup completed successfully!', 'success');
            // Optionally navigate to the new checklist or show it
          }}
          onCancel={() => {
            setIsQuickSetupOpen(false);
            setQuickSetupInitialData(undefined);
          }}
        />
      )}

      {/* Add/Edit Form Modal */}
      {(isAdding || isEditing) && (
        <ChecklistForm
          checklist={(checklistToAdd && checklistToAdd.name) ? checklistToAdd as Checklist : (selectedChecklist || undefined)}
          checklists={checklists.filter(c => c.id !== selectedChecklist?.id)} // Exclude current checklist if editing
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      )}

      {/* Run Checklist Modal */}
      {isRunning && selectedChecklist && (
        <RunChecklist
          checklist={selectedChecklist}
          onClose={handleCloseRunChecklist}
          onComplete={async () => {
            await loadChecklists();
            handleCloseRunChecklist();
            showNotification('Checklist completed successfully!', 'success');
          }}
        />
      )}

      {/* Checklist History Modal */}
      {isViewingHistory && checklistForHistory && (
        <ChecklistHistory
          checklist={checklistForHistory}
          onClose={() => {
            setIsViewingHistory(false);
            setChecklistForHistory(null);
          }}
        />
      )}

      {/* Organization Required Modal */}
      {showOrgModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4"
          onClick={() => setShowOrgModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Organization Required</h3>
                <p className="text-sm text-gray-600">Action Required</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-700 mb-6">
              You must have an organization to add checklists. Please create an organization first.
            </p>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowOrgModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowOrgModal(false);
                  navigate('/organizations');
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium inline-flex items-center gap-2"
              >
                Go to Organizations
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4"
          onClick={() => setShowUpgradeModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Upgrade Required</h3>
                <p className="text-sm text-gray-600">Run Checklist</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-700 mb-6">
              Running checklists requires an <strong>Operate</strong> or <strong>Comply</strong> plan. You can create and manage checklists on your current plan, but to run them and track completion, upgrade now!
            </p>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowUpgradeModal(false);
                  navigate('/subscription');
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium inline-flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Upgrade Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Getting Started button on empty state */}
      {checklists.length === 0 && !isLoading && !showOrgModal && (
        <a
          href="/help/getting-started"
          className="fixed bottom-24 right-4 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg px-4 py-2 z-50"
          title="Getting Started"
        >
          Getting Started
        </a>
      )}
    </div>
  );
};

export default ChecklistManagement;

